import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { AlertCircle, CheckCircle, ExternalLink, Workflow } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  homePathFor,
  useHomePersona,
  type HomePersona,
} from "@shared-core/domain/homePersona";
import type * as API from "@shared-core/data/api-types";
import { paths } from "@shared-core/routing/routes";
import {
  formatCount,
  formatWeekdayDateOrDash,
} from "@shared-core/domain/formatters";
import { HomePersonaToggle } from "../app/components/patterns/HomePersonaToggle";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import { RecommendationCard } from "../app/components/domain/recommendations/RecommendationCard";
import { RecommendationsMetricCard } from "../app/components/domain/recommendations/RecommendationsMetricCard";

/**
 * OpsHome — the CSR home dashboard.
 *
 * Mirrors SalesHome's shape (orders, alerts, opportunities) re-lensed for
 * the Customer Service Rep:
 *   - Orders        → work in progress / on-time health (work portfolio)
 *   - Alerts        → work alerts / exceptions
 *   - Opportunities → reuses the sales opportunities feed for now (no
 *                     distinct ops read-across yet — they're related)
 *
 * Every section composes an existing data feed; nothing new is fetched. The
 * estimates-awaiting-booking card retired with booking (order to invoice
 * step 3b).
 */

type OpportunityPriorityFilter = "all" | "urgent" | "recommended" | "consider";

const OPPORTUNITY_PRIORITIES: {
  id: OpportunityPriorityFilter;
  label: string;
}[] = [
  { id: "all", label: "All" },
  { id: "urgent", label: "Urgent" },
  { id: "recommended", label: "Recommended" },
  { id: "consider", label: "Consider" },
];

// Work-alert severities map to the work-visibility RAG vocabulary
// (red = critical, amber = warning, info = informational), mirroring the
// sales alerts severity filter chips.
type WorkAlertSeverityFilter = "all" | "red" | "amber" | "info";

const WORK_ALERT_SEVERITIES: { id: WorkAlertSeverityFilter; label: string }[] =
  [
    { id: "all", label: "All" },
    { id: "red", label: "Critical" },
    { id: "amber", label: "Warning" },
    { id: "info", label: "Info" },
  ];

const SEVERITY_LABEL = {
  red: "Critical",
  amber: "Warning",
  info: "Info",
} as const;

const SEVERITY_BADGE = {
  red: "bg-red-100 text-red-700 ring-1 ring-red-200",
  amber: "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
  info: "bg-blue-100 text-blue-700 ring-1 ring-blue-200",
} as const;

const RAG_PILL: Record<"green" | "amber" | "red", string> = {
  green: "bg-green-100 text-green-700 ring-1 ring-green-200",
  amber: "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
  red: "bg-red-100 text-red-700 ring-1 ring-red-200",
};

function formatDueDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return formatWeekdayDateOrDash(iso);
}

export function OpsHome() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  // Role only drives the no-preference default; the on-page toggle always
  // sets an explicit persona, so it isn't needed here.
  const { setPersona } = useHomePersona({ role: undefined });
  const switchPersona = (next: HomePersona) => {
    setPersona(next);
    navigate(homePathFor(next));
  };
  const [work, setWork] = useState<API.DashboardWork | null>(null);
  const [home, setHome] = useState<API.DashboardHome | null>(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState<Set<string>>(new Set());

  const [opportunityPriorityFilter, setOpportunityPriorityFilter] =
    useState<OpportunityPriorityFilter>("urgent");
  const [showAllOpportunities, setShowAllOpportunities] = useState(false);
  const [alertSeverityFilter, setAlertSeverityFilter] =
    useState<WorkAlertSeverityFilter>("all");
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const INITIAL_VISIBLE = 3;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [workResult, homeResult] = await Promise.all([
          dataAdapter.getDashboardWork(),
          dataAdapter.getDashboardHome(),
        ]);
        if (cancelled) return;
        setWork(workResult);
        setHome(homeResult);
      } catch (err) {
        console.error("Error loading ops dashboard:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  function removeWorkAlert(alertId: string) {
    setWork((prev) =>
      prev
        ? {
            ...prev,
            work_alerts: (prev.work_alerts ?? []).filter(
              (a) => a.id !== alertId,
            ),
          }
        : prev,
    );
  }

  function clearBusy(alertId: string) {
    setAcking((prev) => {
      const next = new Set(prev);
      next.delete(alertId);
      return next;
    });
  }

  // Dismiss mirrors the sales "Dismiss": clear the alert from the
  // attention list. For work alerts the clearing action is acknowledge.
  async function handleDismiss(alertId: string) {
    setAcking((prev) => new Set(prev).add(alertId));
    try {
      await dataAdapter.acknowledgeWorkAlert(alertId);
      removeWorkAlert(alertId);
    } catch (err) {
      console.error("Error dismissing work alert:", err);
      clearBusy(alertId);
    }
  }

  // Convert-to-ticket mirrors the sales alert flow: spin up a ticket from the
  // alert, then clear it (acknowledge) so it leaves the attention list.
  async function handleConvertToTicket(alert: API.HomeScreenWorkAlert) {
    setAcking((prev) => new Set(prev).add(alert.id));
    try {
      // assigned_to_user_id omitted — the backend assigns to the creator.
      await dataAdapter.createTicket({
        title: alert.work_unit_title || alert.message,
        customer_id: alert.customer_id,
        action_type: "general",
        due_date: undefined,
      });
      await dataAdapter.acknowledgeWorkAlert(alert.id);
      removeWorkAlert(alert.id);
    } catch (err) {
      console.error("Error converting work alert to ticket:", err);
      clearBusy(alert.id);
    }
  }

  if (loading || !work || !home) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading work dashboard...</div>
      </div>
    );
  }

  const cards = work.summary_cards ?? {
    total_active: 0,
    rag_green: 0,
    rag_amber: 0,
    rag_red: 0,
    exception_count_by_severity: {},
  };
  const alerts = (work.work_alerts ?? []) as API.HomeScreenWorkAlert[];
  const alertSeverityCounts = { red: 0, amber: 0, info: 0 };
  for (const a of alerts) {
    const sev = a.severity as keyof typeof alertSeverityCounts;
    if (sev in alertSeverityCounts) alertSeverityCounts[sev]++;
  }
  const filteredAlerts =
    alertSeverityFilter === "all"
      ? alerts
      : alerts.filter((a) => a.severity === alertSeverityFilter);
  const visibleAlerts = showAllAlerts
    ? filteredAlerts
    : filteredAlerts.slice(0, INITIAL_VISIBLE);

  const priCounts = home.opportunity_priority_counts ?? {
    urgent: 0,
    recommended: 0,
    consider: 0,
  };
  const allOpportunities: API.Opportunity[] = home.opportunities ?? [];
  const filteredOpportunities =
    opportunityPriorityFilter === "all"
      ? allOpportunities
      : allOpportunities.filter(
          (o) => o.priority === opportunityPriorityFilter,
        );
  const visibleOpportunities = showAllOpportunities
    ? filteredOpportunities
    : filteredOpportunities.slice(0, INITIAL_VISIBLE);

  const removeOpportunity = (opportunityId: string) => {
    setHome((prev) =>
      prev
        ? {
            ...prev,
            opportunities: (prev.opportunities ?? []).filter(
              (o) => o.id !== opportunityId,
            ),
          }
        : prev,
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-1 gap-4 flex-wrap">
          <h1 className="text-2xl">My work</h1>
          <div className="flex items-center gap-3">
            <HomePersonaToggle current="ops" onSelect={switchPersona} />
            <Link
              to={paths.jobs({ view: "work" })}
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            >
              Open work portfolio
              <ExternalLink className="size-3" />
            </Link>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Jobs in flight, work alerts and opportunities across my customers
        </p>
      </div>

      {/* Top strip: Work in progress */}
      <div className="[&_[data-slot=card]]:shadow-md [&_[data-slot=card]]:border-gray-300">
        <WorkInProgressCard
          cards={cards}
          openIssueCount={work.open_issue_count ?? 0}
          openComplaintCount={work.open_complaint_count ?? 0}
        />
      </div>

      {/* Two-column: Work alerts | Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Work alerts column — mirrors the sales alerts column: metric
            card, divider, heading + severity chips, then the list. */}
        <div>
          <div className="[&_[data-slot=card]]:shadow-md [&_[data-slot=card]]:border-gray-300">
            <WorkAlertsMetricCard
              criticalCount={alertSeverityCounts.red}
              warningCount={alertSeverityCounts.amber}
            />
          </div>
          <div className="border-t border-border mt-6 pt-6 space-y-3">
            <h2 className="text-lg">Work alerts</h2>
            <div className="flex gap-1.5 flex-wrap">
              {WORK_ALERT_SEVERITIES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setAlertSeverityFilter(s.id);
                    setShowAllAlerts(false);
                  }}
                  className={`px-2.5 py-0.5 rounded-full text-xs transition-colors ${
                    alertSeverityFilter === s.id
                      ? "bg-primary text-white"
                      : "bg-surface border border-border text-foreground hover:bg-surface-hover"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          {filteredAlerts.length === 0 ? (
            <div className="text-muted-foreground text-sm p-6 text-center border border-border rounded-lg mt-3">
              No active work alerts.
            </div>
          ) : (
            <div className="space-y-2 mt-3">
              {visibleAlerts.map((alert) => (
                <AlertRow
                  key={alert.id}
                  alert={alert}
                  busy={acking.has(alert.id)}
                  onConvertToTicket={() => handleConvertToTicket(alert)}
                  onDismiss={() => handleDismiss(alert.id)}
                />
              ))}
              {!showAllAlerts && filteredAlerts.length > INITIAL_VISIBLE && (
                <button
                  type="button"
                  onClick={() => setShowAllAlerts(true)}
                  className="text-sm text-primary hover:underline"
                >
                  Show {filteredAlerts.length - INITIAL_VISIBLE} more
                </button>
              )}
            </div>
          )}
        </div>

        {/* Opportunities column — reuses the sales opportunities feed */}
        <div className="border-t border-border pt-8 mt-4 lg:border-t-0 lg:pt-0 lg:mt-0">
          <div className="[&_[data-slot=card]]:shadow-md [&_[data-slot=card]]:border-gray-300">
            <RecommendationsMetricCard
              urgentCount={priCounts.urgent}
              recommendedCount={priCounts.recommended}
            />
          </div>
          <div className="border-t border-border mt-6 pt-6 space-y-3">
            <h2 className="text-lg">Opportunities</h2>
            <div className="flex gap-1.5 flex-wrap">
              {OPPORTUNITY_PRIORITIES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setOpportunityPriorityFilter(p.id);
                    setShowAllOpportunities(false);
                  }}
                  className={`px-2.5 py-0.5 rounded-full text-xs transition-colors ${
                    opportunityPriorityFilter === p.id
                      ? "bg-primary text-white"
                      : "bg-surface border border-border text-foreground hover:bg-surface-hover"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {filteredOpportunities.length === 0 ? (
            <div className="text-muted-foreground text-sm p-6 text-center border border-border rounded-lg mt-3">
              No opportunities
            </div>
          ) : (
            <div className="space-y-3 mt-3">
              {visibleOpportunities.map((action) => (
                <RecommendationCard
                  key={action.id}
                  action={action}
                  onCreateTicket={async () => {
                    // assigned_to_user_id omitted — the backend assigns to the creator.
                    const newTicket = await dataAdapter.createTicket({
                      title: action.title,
                      customer_id: action.customer_id,
                      opportunity_id: action.id,
                      action_type: action.suggested_action_type,
                      due_date: action.due_date || undefined,
                    });
                    await dataAdapter.updateOpportunity(action.id, {
                      status: "converted_to_task",
                      converted_to_task_id: newTicket.id,
                    });
                    removeOpportunity(action.id);
                  }}
                  onNotRelevant={async () => {
                    await dataAdapter.updateOpportunity(action.id, {
                      status: "dismissed",
                    });
                    removeOpportunity(action.id);
                  }}
                  onViewTicket={() => navigate(paths.tickets())}
                />
              ))}
              {!showAllOpportunities &&
                filteredOpportunities.length > INITIAL_VISIBLE && (
                  <button
                    type="button"
                    onClick={() => setShowAllOpportunities(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    Show {filteredOpportunities.length - INITIAL_VISIBLE} more
                  </button>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Work-in-progress card (orders lens). Mirrors the wide ContributionTargetCard
 * slot in SalesHome — but shows on-time health of active jobs instead of £.
 * Each count deep-links into the work portfolio with the matching RAG filter.
 */
function WorkInProgressCard({
  cards,
  openIssueCount,
  openComplaintCount,
}: {
  cards: API.PortfolioSummaryCards;
  openIssueCount: number;
  openComplaintCount: number;
}) {
  return (
    <Link to={paths.jobs({ view: "work" })} className="block h-full">
      <Card className="p-4 h-full flex flex-col hover:bg-muted/50 transition-colors cursor-pointer">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
              <Workflow className="size-4" />
              Work in progress
            </div>
            <div className="text-3xl font-bold text-foreground">
              {formatCount(cards.total_active)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              active jobs
            </div>
          </div>
        </div>

        {/* rag_* are the work-alert rollup (bool_or(severity=...) over
            uncleared alerts, see work_portfolio_query.py), not deadline
            signals — so the approved trio is "no alerts" / "warning" /
            "overdue": red is honestly overdue-only now that the one
            red-severity rule is "Past due date" and the detector that used
            to wrongly escalate amber to red off a raw due_at timestamp has
            been fixed. Same wording fixed on the portfolio board
            (PortfolioBoard.tsx's `boardFlag`) and the work portfolio page. */}
        <div className="flex items-center gap-4 text-sm tabular-nums mt-auto pt-2">
          <span className="text-green-700">
            {formatCount(cards.rag_green)} no alerts
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="text-amber-700">
            {formatCount(cards.rag_amber)} warning
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="text-red-700">
            {formatCount(cards.rag_red)} overdue
          </span>
        </div>
        {/* Issue indicators — customer-attached open issues across the
            portfolio, with complaints called out as the concerning subset.
            Distinct from RAG: these track quality/relationship attention,
            not delivery timing. */}
        <div className="flex items-center gap-4 text-sm tabular-nums pt-1">
          <span className="text-muted-foreground">
            {formatCount(openIssueCount)} issues
          </span>
          <span className="text-muted-foreground">·</span>
          <span
            className={
              openComplaintCount > 0 ? "text-rose-700" : "text-muted-foreground"
            }
          >
            {formatCount(openComplaintCount)} complaints
          </span>
        </div>
      </Card>
    </Link>
  );
}

/**
 * Work-alerts summary card. Mirrors the sales AlertsMetricCard aesthetic —
 * big count + "X critical • Y warning" sub-line, severity-tinted icon — but
 * uses the work-visibility RAG vocabulary (red = critical, amber = warning).
 * The count excludes info-level alerts, matching how the sales card excludes
 * medium/low.
 */
function WorkAlertsMetricCard({
  criticalCount,
  warningCount,
}: {
  criticalCount: number;
  warningCount: number;
}) {
  const total = criticalCount + warningCount;
  const hasCritical = criticalCount > 0;
  return (
    <Link to={paths.jobs({ view: "work" })}>
      <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
              <AlertCircle className="size-4" />
              Work alerts
            </div>
            <div className="text-3xl font-bold text-foreground">
              {formatCount(total)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {total === 0
                ? "No active work alerts"
                : `${formatCount(criticalCount)} critical • ${formatCount(warningCount)} warning`}
            </div>
          </div>
          <div
            className={`p-2 rounded-full ${
              hasCritical
                ? "bg-red-500/10"
                : total > 0
                  ? "bg-amber-500/10"
                  : "bg-green-500/10"
            }`}
          >
            <AlertCircle
              className={`size-5 ${
                hasCritical
                  ? "text-red-600"
                  : total > 0
                    ? "text-amber-600"
                    : "text-green-600"
              }`}
            />
          </div>
        </div>

        <div className="text-xs text-primary inline-flex items-center gap-1">
          Open work portfolio
          <ExternalLink className="size-3" />
        </div>
      </Card>
    </Link>
  );
}

interface AlertRowProps {
  alert: API.HomeScreenWorkAlert;
  busy: boolean;
  onConvertToTicket: () => void;
  onDismiss: () => void;
}

function AlertRow({
  alert,
  busy,
  onConvertToTicket,
  onDismiss,
}: AlertRowProps) {
  const navigate = useNavigate();
  const timeAgo = formatDistanceToNow(new Date(alert.detected_at), {
    addSuffix: true,
  });
  const severityClass =
    SEVERITY_BADGE[alert.severity as keyof typeof SEVERITY_BADGE] ??
    SEVERITY_BADGE.info;
  const severityLabel =
    SEVERITY_LABEL[alert.severity as keyof typeof SEVERITY_LABEL] ??
    alert.severity;
  const ragClass = RAG_PILL[alert.rag] ?? RAG_PILL.green;
  // Job title is the headline; fall back to the alert message when
  // the title is missing (alerts not tied to a chargeable_work row).
  const title = alert.work_unit_title || alert.message;

  // Whole-tile click opens the work unit, mirroring the sales alert tile
  // (which opens a detail view). Inner links/buttons stop propagation.
  const openWorkUnit = () => {
    if (alert.work_unit_url) navigate(alert.work_unit_url);
  };

  return (
    <div
      onClick={openWorkUnit}
      role={alert.work_unit_url ? "link" : undefined}
      tabIndex={alert.work_unit_url ? 0 : undefined}
      aria-label={alert.work_unit_url ? `Open ${title}` : undefined}
      onKeyDown={(e) => {
        if (alert.work_unit_url && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          openWorkUnit();
        }
      }}
      className={`flex items-start gap-3 p-4 border border-border rounded-lg bg-card shadow-sm hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none transition-colors ${
        alert.work_unit_url ? "cursor-pointer" : ""
      }`}
    >
      <AlertCircle className="size-5 text-destructive shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h4 className="font-medium truncate">
            {alert.job_no ? (
              <>
                <span className="tabular-nums">Job {alert.job_no}</span>
                {title && <span className="text-muted-foreground"> — </span>}
                {title && <span>{title}</span>}
              </>
            ) : (
              title
            )}
          </h4>
          <span
            className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase ${severityClass}`}
          >
            {severityLabel}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mb-2">{alert.message}</p>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
            {alert.customer_url && alert.customer_name && (
              <>
                <Link
                  to={alert.customer_url}
                  onClick={(e) => e.stopPropagation()}
                  className="hover:underline text-[color:var(--color-primary)]"
                >
                  {alert.customer_name}
                </Link>
                <span>•</span>
              </>
            )}
            {alert.required_at && (
              <>
                <span>Due {formatDueDate(alert.required_at)}</span>
                <span>•</span>
              </>
            )}
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${ragClass}`}
            >
              {alert.rag}
            </span>
            <span>•</span>
            <span>{timeAgo}</span>
            <span>•</span>
            <span className="font-mono opacity-70">{alert.rule_code}</span>
          </div>
          <div
            className="shrink-0 flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              size="sm"
              onClick={onConvertToTicket}
              disabled={busy}
            >
              <CheckCircle className="size-3.5" />
              Convert to ticket
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              disabled={busy}
            >
              {busy ? "…" : "Dismiss"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
